import {
  Col,
  Row,
  TableColumnsType,
  Button,
  Typography,
  Form,
  Space,
  Table,
  Input
} from 'antd';
import { css } from '@emotion/react';
import DISH_STATISTICS from '@/constants/dish-statistics.constant';
import { useEffect, useState } from 'react';
import {
  getDetailDish,
  watchDetailDishById
} from '@/redux/slices/dish.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PageURLs } from '@/utils/navigate';
type DishInformationModalProps = {
  dish: { id: string; ageGroupId: string, dishSizeId: string };
  onCancel: () => void;
  listDishAge: DishAge[];
  listSourceDish: ListSourceDish[];
  listSizeDish: SizeDish[];
  listRegion: RegionDish[];
  listTagDish: TagDish[];
};

// type DataRecordDish = {
//   ingredient: { name: string },
//   weight: { name: string },
//   processingMethod: { name: string },
// }

type DataRecordDish = {
  carbohydrate: string;
  energy: string;
  fat: string;
  fruit: string;
  id: string;
  ingredientId: string;
  ingredientName: string;
  processingMethod: string;
  protein: string;
  salt: string;
  vegetable: string;
  weight: string;
};

import { PermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';

export default function DishInformationModal({
  dish,
  onCancel,
  listDishAge,
  listSourceDish,
  listSizeDish,
  listRegion,
  listTagDish
}: DishInformationModalProps) {
  const [form] = Form.useForm();
  const [dataRecordDish, setDataRecordDish] = useState<DataRecordDish[]>([]);
  const dispatch = useAppDispatch();
  const { push } = useQuery();
  const detailDish = useAppSelector(watchDetailDishById);

  useEffect(() => {
    dispatch(getDetailDish({ id: dish.id, ageGroupId: dish.ageGroupId, dishSizeId: dish.dishSizeId }));
  }, [dispatch, dish]);

  const statisticColumns: TableColumnsType<any> = Object.keys(
    DISH_STATISTICS
  ).map((key, _) => ({
    title: DISH_STATISTICS[key].title,
    dataIndex: key,
    key,
    width: 1,
    render: (_, record) =>
      record[DISH_STATISTICS[key]?.name] &&
      record[DISH_STATISTICS[key]?.name] + DISH_STATISTICS[key]?.unit
  }));

  const labelGroupOptions = listTagDish.map((item) => ({
    value: item.tagName,
    label: item.tagName
  }));

  const tagLabel = labelGroupOptions
    .filter((item) => detailDish?.tags?.includes(item.value))
    .map((item) => item.label)
    .join(', ');

  const ageLabel = listDishAge.find((item) => item.id == detailDish?.ageGroupId)?.name || '';
  const sizeLabel = listSizeDish.find((item) => item.id == detailDish?.sizes?.[0]?.sizeType)?.name || '';
  const sourceDishLabel = listSourceDish.find((item) => item.id === detailDish?.externalDishTypeId)?.name || '';
  const regionLabel = listRegion.find((item) => item.regionId === detailDish?.regionId)?.regionName || '';

  useEffect(() => {
    const formulaTypes =
      detailDish?.sizes?.[0]?.saltFormulas?.map((sf) => sf.formulaType).join(", ") || "";
    form.setFieldsValue({
      age: ageLabel,
      sourceDish: sourceDishLabel,
      label: tagLabel,
      region: regionLabel,
      size: sizeLabel,
      saltRecipe: formulaTypes
    });
  }, [ageLabel, sourceDishLabel, tagLabel, regionLabel, sizeLabel, dish, form, detailDish]);

  const onEdit = () => {
    push(
      `${PageURLs.ofDishManagement()}?mode=${PermissionMode.EDIT}&dishId=${dish.id}&ageGroupId=${dish.ageGroupId}&dishSizeId=${dish.dishSizeId}`
    );
    onCancel();
  }

  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      render: (_, __, index) => index + 1,
      width: 1
    },
    {
      title: 'Thành phần',
      dataIndex: 'ingredientName',
      key: 'ingredientName',
      width: 200,
      fixed: 'left'
    },
    {
      title: 'Khối lượng',
      dataIndex: 'weight',
      key: 'weight',
      render: (value) => value + 'g',
      width: 1
    },
    ...(statisticColumns as TableColumnsType<FoodNutrient>)
  ];

  useEffect(() => {
    const newData: DataRecordDish[] = (detailDish?.sizes ?? []).flatMap((size) =>
      (size.saltFormulas ?? []).flatMap((formula) =>
        (formula.ingredients ?? []).map((item) => ({
          id: item.id ?? '',
          ingredientId: item.ingredientId ?? '',
          ingredientName: item.ingredientName ?? '',
          processingMethod: item.processingMethod ?? '',
          weight: item.weight ?? '',
          energy: item.energy ?? '',
          carbohydrate: item.carbohydrate ?? '',
          protein: item.protein ?? '',
          fat: item.fat ?? '',
          vegetable: item.vegetable ?? '',
          fruit: item.fruit ?? '',
          salt: item.salt ?? ''
        }))
      )
    );

    setDataRecordDish(newData);
  }, [detailDish, setDataRecordDish]);

  return (
    <Form form={form} validateTrigger='onSubmit' css={containerStyles}>
      <Typography.Title css={titleStyles} level={5}>
        Thông tin món ăn
      </Typography.Title>

      <Row align={'middle'} justify={'center'} gutter={32}>
        <Col>
          <img
            src={detailDish?.sizes?.[0]?.image || IMAGE_DEFAULT}
            style={{ width: '200px', aspectRatio: '3/2', objectFit: 'cover' }}
          />
        </Col>
        <Typography.Title level={4}>{detailDish?.name}</Typography.Title>
      </Row>

      <Row align={'top'} gutter={16} css={fieldStyles}>
        <Col span={12}>
          <Form.Item name='age' label='Độ tuổi' labelCol={{ span: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='region' label='Món ăn miền' labelCol={{ span: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
      </Row>
      <Row align={'top'} gutter={16} css={fieldStyles}>
        <Col span={12}>
          <Form.Item
            name='sourceDish'
            label='Nguồn món ăn bên ngoài'
            labelCol={{ span: 8 }}
          >
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='size' label='Cỡ món ăn' labelCol={{ span: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
      </Row>
      <Row align={'top'} gutter={16} css={fieldStyles}>
        <Col span={12}>
          <Form.Item
            name='saltRecipe'
            label='Công thức muối'
            labelCol={{ span: 8 }}
          >
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='label' label='Nhãn món ăn' labelCol={{ span: 8 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
      </Row>

      <Table<any>
        columns={columns}
        dataSource={dataRecordDish}
        pagination={false}
      />

      <Form.Item>
        <Space css={groupButtonStyles}>
          <Button type='default' size='large' onClick={onCancel}>
            Đóng
          </Button>
          <Button type='primary' size='large' onClick={onEdit}>
            Chỉnh sửa
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-height: 90vh;

  overflow-y: auto;
  overflow-x: hidden;  
  -ms-overflow-style: none; 
  scrollbar-width: none;  

  &::-webkit-scrollbar {
    display: none; 
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const titleStyles = css`
  margin-bottom: 1.6rem !important;
  line-height: 3.2rem !important;
`;

const fieldStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-form-item {
    width: 100%;
    margin-bottom: 6px;

    .ant-form-item-row {
      display: flex;
      justify-content: space-between;
    }

    .ant-form-item-label {
      text-align: start !important;

      label {
        font-weight: bold;
        white-space: normal;
        height: auto;
        line-height: 1.5;

        &::after,
        &::before {
          display: none !important;
        }
      }
    }
  }
`;

const groupButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.6rem;
  margin-top: 1.6rem;

  button {
    padding: 2rem 4rem;
  }
`;
